/**
 * PLAYER BOOTSTRAP STORE
 * 
 * Loads all common player baseline data ONCE per session with:
 * - In-memory cache with TTL
 * - In-flight request deduplication
 * - Session-validated RPC calls
 * 
 * This consolidates multiple individual fetches into a single cached load.
 */

import { supabase } from '@/integrations/supabase/client';
import { SessionManager } from '@/services/sessionManager';
import { rpcWithSessionCached, invalidateCacheByRpc } from '@/services/rpcClient';
import { loadFinancialStatus as loadFromCentralStore } from '@/stores/financialStatusStore';
import { log } from '@/lib/log';

// Types
export interface CharacterProfile {
  name: string;
  skinTone: string;
  gender: string;
  hairstyle: string;
  hairColor: string;
  clothing: string;
  appearance?: Record<string, any>;
  selectedTeacherAvatarId?: string | null;
}

export interface FinancialStatus {
  current_money: number;
  total_earned: number;
  total_spent: number;
  current_time_period_id: string | null;
}

export interface OnboardingStatus {
  step: 'avatar' | 'teacher_avatar' | 'introduction' | 'completed';
  completed_at: string | null;
}

export interface StreakData {
  current_streak: number;
  best_streak: number;
  total_correct_answers: number;
  last_answer_time: string | null;
  streak_multiplier: number;
}

export interface UserProgressState {
  currentLocation: string;
  totalPoints: number;
  money: number;
  level: number;
  completedScenarios: string[];
  unlockedLocations: string[];
}

export interface PlayerBootstrap {
  characterProfile: CharacterProfile | null;
  financialStatus: FinancialStatus | null;
  onboarding: OnboardingStatus | null;
  streaks: StreakData | null;
  userProgressState: UserProgressState | null;
  loadedAt: number;
}

// Default values
const DEFAULT_CHARACTER: CharacterProfile = {
  name: 'Pelaaja',
  skinTone: '#FDBCB4',
  gender: 'male',
  hairstyle: 'short',
  hairColor: '#8B4513',
  clothing: '#FF6B35',
  appearance: {
    hairColor: '#8B4513',
    skinTone: '#FDBCB4',
    outfit: '#FF6B35',
    accessories: [],
    pants: '#4A90E2'
  }
};

const DEFAULT_PROGRESS: UserProgressState = {
  currentLocation: 'home',
  totalPoints: 0,
  money: 1500,
  level: 1,
  completedScenarios: [],
  unlockedLocations: ['home', 'bank', 'supermarket', 'cafe']
};

// Cache configuration
const CACHE_TTL_MS = 120_000; // 2 minutes

// Store state
let cache: Map<string, { data: PlayerBootstrap; at: number }> = new Map();
let inFlight: Map<string, Promise<PlayerBootstrap>> = new Map();
let lastLoggedMissing: Map<string, number> = new Map();

// Helpers
function getCacheKey(playerId: string): string {
  return `bootstrap:${playerId}`;
}

function isCacheValid(entry: { data: PlayerBootstrap; at: number } | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.at < CACHE_TTL_MS;
}

// Fetch individual data pieces
async function fetchCharacterProfile(
  playerId: string, 
  sessionToken: string
): Promise<CharacterProfile | null> {
  try {
    const { data, error } = await rpcWithSessionCached(
      'get_character_profile',
      (s) => ({ p_player_id: playerId, p_session_token: s.sessionToken }),
      { ttlMs: CACHE_TTL_MS, key: `char:${playerId}`, reason: 'bootstrap' }
    );
    
    if (error) {
      log('[bootstrap] fetchCharacterProfile RPC error:', error);
      return null;
    }
    
    if (!data) {
      log('[bootstrap] fetchCharacterProfile: no data returned');
      return null;
    }
    
    const profile = data as any;
    log('[bootstrap] fetchCharacterProfile loaded:', { 
      name: profile.name, 
      gender: profile.gender,
      hairstyle: profile.hairstyle 
    });
    
    return {
      name: profile.name || DEFAULT_CHARACTER.name,
      skinTone: profile.skin_tone || DEFAULT_CHARACTER.skinTone,
      gender: profile.gender || DEFAULT_CHARACTER.gender,
      hairstyle: profile.hairstyle || DEFAULT_CHARACTER.hairstyle,
      hairColor: profile.hair_color || DEFAULT_CHARACTER.hairColor,
      clothing: profile.clothing || DEFAULT_CHARACTER.clothing,
      appearance: profile.appearance,
      selectedTeacherAvatarId: profile.selected_teacher_avatar_id
    };
  } catch (err) {
    log('[bootstrap] fetchCharacterProfile error:', err);
    return null;
  }
}

async function fetchFinancialStatus(
  playerId: string,
  sessionToken: string
): Promise<FinancialStatus | null> {
  try {
    // Use centralized store - handles caching, dedupe, and mount cooldown
    const result = await loadFromCentralStore({
      playerId,
      sessionToken,
      reason: 'bootstrap-initial'
    });
    
    if (!result) return null;
    
    return {
      current_money: result.current_money ?? 0,
      total_earned: result.total_earned ?? 0,
      total_spent: result.total_spent ?? 0,
      current_time_period_id: result.current_time_period_id ?? null
    };
  } catch (err) {
    log('[bootstrap] fetchFinancialStatus error:', err);
    return null;
  }
}

async function fetchOnboardingStatus(
  playerId: string,
  sessionToken?: string
): Promise<OnboardingStatus | null> {
  try {
    // Use SECURITY DEFINER RPC when we have a session token. Direct table reads
    // depend on a per-connection GUC that PostgREST's pooled connections do not
    // reliably carry, which previously caused onboarding to fall back to 'avatar'
    // after completing later steps and looped the user back to character creation.
    if (sessionToken) {
      const { data, error } = await supabase.rpc('get_player_onboarding_status', {
        p_session_token: sessionToken,
        p_player_id: playerId,
      });
      if (error) {
        log('[bootstrap] fetchOnboardingStatus RPC error:', error);
        // Do NOT silently fall back to { step: 'avatar' } — that caused the loop.
        return null;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        return { step: 'avatar', completed_at: null };
      }
      return {
        step: (row.step as OnboardingStatus['step']) || 'avatar',
        completed_at: row.completed_at ?? null,
      };
    }

    // Legacy fallback: direct read (only used if a caller has no session token).
    const { data, error } = await supabase
      .from('player_onboarding')
      .select('step, completed_at')
      .eq('player_id', playerId)
      .maybeSingle();

    if (error) {
      log('[bootstrap] fetchOnboardingStatus direct error:', error);
      return null;
    }

    return data ? {
      step: (data.step as OnboardingStatus['step']) || 'avatar',
      completed_at: data.completed_at
    } : { step: 'avatar', completed_at: null };
  } catch (err) {
    log('[bootstrap] fetchOnboardingStatus error:', err);
    return null;
  }
}

async function fetchStreaks(playerId: string): Promise<StreakData | null> {
  try {
    const { data, error } = await supabase
      .from('player_streaks')
      .select('*')
      .eq('player_id', playerId)
      .maybeSingle();
    
    if (error) {
      log('[bootstrap] fetchStreaks error:', error);
      return null;
    }
    
    return data ? {
      current_streak: data.current_streak ?? 0,
      best_streak: data.best_streak ?? 0,
      total_correct_answers: data.total_correct_answers ?? 0,
      last_answer_time: data.last_answer_time ?? null,
      streak_multiplier: Number(data.streak_multiplier ?? 1)
    } : null;
  } catch (err) {
    log('[bootstrap] fetchStreaks error:', err);
    return null;
  }
}

async function fetchUserProgressState(playerId: string): Promise<UserProgressState | null> {
  try {
    const { data, error } = await supabase
      .from('user_progress_state')
      .select('progress_data')
      .eq('player_id', playerId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      log('[bootstrap] fetchUserProgressState error:', error);
      return null;
    }
    
    if (!data?.progress_data) {
      return DEFAULT_PROGRESS;
    }
    
    const pd = data.progress_data as any;
    return {
      currentLocation: pd.currentLocation || 'home',
      totalPoints: pd.totalPoints || 0,
      money: pd.money || 1500,
      level: pd.level || 1,
      completedScenarios: pd.completedScenarios || [],
      unlockedLocations: pd.unlockedLocations || ['home', 'bank', 'supermarket', 'cafe']
    };
  } catch (err) {
    log('[bootstrap] fetchUserProgressState error:', err);
    return null;
  }
}

// Main API
export async function loadPlayerBootstrap(options: { 
  playerId: string; 
  sessionToken: string;
  reason?: string;
}): Promise<PlayerBootstrap> {
  const { playerId, sessionToken, reason = 'unknown' } = options;
  
  if (!playerId || !sessionToken) {
    log('[bootstrap] missing playerId or sessionToken, skipping');
    return {
      characterProfile: null,
      financialStatus: null,
      onboarding: null,
      streaks: null,
      userProgressState: null,
      loadedAt: Date.now()
    };
  }
  
  const key = getCacheKey(playerId);
  
  // Check cache
  const cached = cache.get(key);
  if (isCacheValid(cached)) {
    log('[bootstrap] cache hit', { playerId, reason, age: Date.now() - cached!.at });
    return cached!.data;
  }
  
  // Check in-flight
  const existing = inFlight.get(key);
  if (existing) {
    log('[bootstrap] in-flight reused', { playerId, reason });
    return existing;
  }
  
  // Fetch all data in parallel
  const start = Date.now();
  const promise = (async (): Promise<PlayerBootstrap> => {
    try {
      const [characterProfile, financialStatus, onboarding, streaks, userProgressState] = 
        await Promise.all([
          fetchCharacterProfile(playerId, sessionToken),
          fetchFinancialStatus(playerId, sessionToken),
          fetchOnboardingStatus(playerId, sessionToken),
          fetchStreaks(playerId),
          fetchUserProgressState(playerId)
        ]);
      
      const bootstrap: PlayerBootstrap = {
        characterProfile,
        financialStatus,
        onboarding,
        streaks,
        userProgressState,
        loadedAt: Date.now()
      };
      
      log('[bootstrap] fetched', { 
        playerId, 
        reason, 
        ms: Date.now() - start,
        hasCharacter: !!characterProfile,
        hasFinancial: !!financialStatus,
        hasOnboarding: !!onboarding,
        hasStreaks: !!streaks,
        hasProgress: !!userProgressState
      });
      
      // Cache result
      cache.set(key, { data: bootstrap, at: Date.now() });
      
      return bootstrap;
    } finally {
      inFlight.delete(key);
    }
  })();
  
  inFlight.set(key, promise);
  return promise;
}

export function getCachedPlayerBootstrap(playerId: string): PlayerBootstrap | null {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (isCacheValid(cached)) {
    return cached!.data;
  }
  return null;
}

export function clearPlayerBootstrapCache(playerId?: string): void {
  if (playerId) {
    cache.delete(getCacheKey(playerId));
    // Also invalidate the individual RPC caches
    invalidateCacheByRpc('get_character_profile');
    invalidateCacheByRpc('get_or_create_financial_status_with_session');
    log('[bootstrap] cache cleared for', playerId);
  } else {
    cache.clear();
    invalidateCacheByRpc('get_character_profile');
    invalidateCacheByRpc('get_or_create_financial_status_with_session');
    log('[bootstrap] all caches cleared');
  }
}

// Update specific parts of cached bootstrap
export function updateCachedCharacterProfile(playerId: string, profile: CharacterProfile): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached) {
    cached.data.characterProfile = profile;
    cache.set(key, cached);
  }
}

export function updateCachedFinancialStatus(playerId: string, status: FinancialStatus): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached) {
    cached.data.financialStatus = status;
    cache.set(key, cached);
  }
}

export function updateCachedOnboarding(playerId: string, onboarding: OnboardingStatus): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached) {
    cached.data.onboarding = onboarding;
    cache.set(key, cached);
  }
}

export function updateCachedStreaks(playerId: string, streaks: StreakData): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached) {
    cached.data.streaks = streaks;
    cache.set(key, cached);
  }
}

export function updateCachedUserProgress(playerId: string, progress: UserProgressState): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached) {
    cached.data.userProgressState = progress;
    cache.set(key, cached);
  }
}

/**
 * Update cached teacher avatar selection
 * Call this after successfully selecting a new teacher avatar
 */
export function updateCachedTeacherAvatarId(playerId: string, teacherAvatarId: string): void {
  const key = getCacheKey(playerId);
  const cached = cache.get(key);
  if (cached && cached.data.characterProfile) {
    cached.data.characterProfile.selectedTeacherAvatarId = teacherAvatarId;
    cache.set(key, cached);
    log('[bootstrap] updated cached teacher avatar', { playerId: playerId.slice(-6), teacherAvatarId: teacherAvatarId.slice(-6) });
  }
  
  // Also invalidate the RPC cache for character profile
  invalidateCacheByRpc('get_character_profile');
}

// Export for debugging
export function getBootstrapCacheStats(): { cacheSize: number; inFlightSize: number } {
  return {
    cacheSize: cache.size,
    inFlightSize: inFlight.size
  };
}
